import { SafeLocalStorage } from '#pplx/frontend/browser-storage/localStorage.js';
// This store IS the SDK's assignment-log dedup cache (installed via useCustomAssignmentCache),
// keyed like the SDK's own: one arm per (subject, flag). The SDK consults it only for
// logging-eligible allocations, so a served experiment arm always reaches it — as a fresh
// `set` the first time, as a `has` hit afterwards — with the "what Eppo reports" filter
// intact. That is what makes the record survive reloads and pre-existing browsers without
// depending on the logger callback, which the dedup suppresses.
const STORAGE_KEY = 'pplx.eppo.logged-assignments.v1';
const MAX_ENTRIES = 500;
// The SDK stores these when it logs an evaluation with no allocation/variation.
const SDK_PLACEHOLDER_PREFIX = '__eppo_';
let loaded;
// Only arms the SDK served in this page session are reported: a persisted arm for a flag
// nothing read this session did not shape this request.
const seenThisSession = new Set();
const keyOf = (subjectKey, flagKey) => JSON.stringify([subjectKey, flagKey]);
const parseKey = (key) => JSON.parse(key);
// A flag belongs to whichever subject the SDK evaluated it for last: after a session change
// (login/logout without a reload) the previous identity's arms must stop being reported.
function markServed(subjectKey, flagKey, served) {
    for (const key of seenThisSession) {
        const [seenSubject, seenFlag] = parseKey(key);
        if (seenFlag === flagKey && seenSubject !== subjectKey) {
            seenThisSession.delete(key);
        }
    }
    const key = keyOf(subjectKey, flagKey);
    if (served) {
        seenThisSession.add(key);
    }
    else {
        seenThisSession.delete(key);
    }
}
const isStoredArm = (value) => typeof value === 'object' &&
    value !== null &&
    typeof value.allocation === 'string' &&
    typeof value.variation === 'string';
function load() {
    if (loaded) {
        return loaded;
    }
    loaded = new Map();
    try {
        const parsed = JSON.parse(SafeLocalStorage.getItem(STORAGE_KEY, { global: true }) ?? '{}');
        if (typeof parsed === 'object' && parsed !== null) {
            for (const [key, arm] of Object.entries(parsed)) {
                if (isStoredArm(arm)) {
                    loaded.set(key, arm);
                }
            }
        }
    }
    catch {
        // A corrupt entry must never break flag evaluation; the SDK simply re-logs.
    }
    return loaded;
}
function persist(map) {
    SafeLocalStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(map)), {
        global: true,
    });
}
const matches = (arm, entry) => arm !== undefined &&
    arm.allocation === entry.allocationKey &&
    arm.variation === entry.variationKey;
export const eppoAssignmentCache = {
    has(entry) {
        const key = keyOf(entry.subjectKey, entry.flagKey);
        if (!matches(load().get(key), entry)) {
            return false;
        }
        markServed(entry.subjectKey, entry.flagKey, true);
        return true;
    },
    set(entry) {
        if (entry.allocationKey.startsWith(SDK_PLACEHOLDER_PREFIX) ||
            entry.variationKey.startsWith(SDK_PLACEHOLDER_PREFIX)) {
            return;
        }
        const map = load();
        const key = keyOf(entry.subjectKey, entry.flagKey);
        map.delete(key);
        map.set(key, { allocation: entry.allocationKey, variation: entry.variationKey });
        for (const oldest of map.keys()) {
            if (map.size <= MAX_ENTRIES) {
                break;
            }
            map.delete(oldest);
        }
        markServed(entry.subjectKey, entry.flagKey, true);
        persist(map);
    },
};
// Reconcile a stored arm against what the SDK just served. The dedup cache is never
// consulted when the subject fell out of the allocation, so this is the only signal that a
// stored arm went stale: any mismatch drops it (a re-logged arm re-adds itself via `set`).
export const recordEvaluation = (outcome) => {
    const map = load();
    const key = keyOf(outcome.subjectKey, outcome.flagKey);
    const stored = map.get(key);
    const served = matches(stored, outcome);
    markServed(outcome.subjectKey, outcome.flagKey, served);
    if (stored === undefined || served) {
        return;
    }
    map.delete(key);
    persist(map);
};
// The evaluation context changed (login, logout, account switch): nothing served so far
// applies to the new identity until the SDK evaluates it again. Stored arms stay for dedup.
export const resetServedAssignments = () => {
    seenThisSession.clear();
};
// A flag whose subject is missing (logged out in another tab) cannot be evaluated for the
// current identity, so no previously served arm for it applies either.
export const forgetServedFlag = (flagKey) => {
    for (const key of seenThisSession) {
        if (parseKey(key)[1] === flagKey) {
            seenThisSession.delete(key);
        }
    }
};
export const getLoggedExperimentAssignments = () => Object.fromEntries([...load()]
    .filter(([key]) => seenThisSession.has(key))
    .map(([key, { allocation, variation }]) => {
    const [, flagKey] = parseKey(key);
    return [`${flagKey}-${allocation}`, variation];
}));
//# sourceMappingURL=assignmentStore.js.map